/**
 * @file FaqLookFields.tsx
 * @description Оформление «Вопросов и ответов» в группе «Цвет и шрифт»
 * перед частями текста (phase19.16, партия SB120, этап 3б;
 * uiux-sb120-stage3 §3 «Вопросы и ответы»; данные db-design-sb120-stage3 §1
 * `faq.divider`, `faq.icon`): разделитель — толщина 0–4 px и цвет «Авто:
 * линия» (`DividerFields`, ↺ 1 px); значок — схемами [+ поворот][⌄ стрелка]
 * [нет] (`FAQ_ICONS`), нет ключа — «+» с поворотом, как было.
 */

import { FAQ_ICON_LABELS, FAQ_ICONS, SECTION_LOOK_LIMITS, type FaqIcon, type SectionElementOf } from "@chaiberi/shared";
import { ChevronDown, Minus, Plus } from "lucide-react";

import { Label } from "../../../ui/label";
import { OptionCards } from "../../../site-view/OptionCards";
import { DividerFields } from "./DividerFields";

type Faq = SectionElementOf<"faq">;

/** Образец значка у строки вопроса. */
const ICON_PREVIEW: Readonly<Record<FaqIcon, typeof Plus | null>> = { plus: Plus, chevron: ChevronDown, none: null };

function IconScheme({ icon }: { icon: FaqIcon }) {
  const Icon = ICON_PREVIEW[icon];
  return (
    <span aria-hidden className="flex w-full items-center gap-1">
      <span className="h-1.5 flex-1 rounded-full bg-primary/40" />
      {Icon ? <Icon className="size-4 text-primary" /> : <Minus className="size-4 text-transparent" />}
    </span>
  );
}

const ICONS = FAQ_ICONS.map((value) => ({ value, label: FAQ_ICON_LABELS[value], preview: <IconScheme icon={value} /> }));

interface Props {
  element: Faq;
  onChange: (element: Faq) => void;
  path: string;
}

export function FaqLookFields({ element, onChange, path }: Props) {
  const set = <K extends "divider" | "icon">(key: K, v: Faq[K] | undefined) => {
    const next = { ...element };
    if (v === undefined) delete next[key];
    else next[key] = v;
    onChange(next);
  };
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <DividerFields label="Разделитель" colorLabel="Цвет разделителя" value={element.divider} onChange={(d) => set("divider", d)} range={SECTION_LOOK_LIMITS.faqDivider} path={`${path}.divider`} />
      <div className="flex min-w-0 flex-col gap-1">
        <Label>Значок</Label>
        <OptionCards aria-label="Значок вопроса" columns={3} options={ICONS} value={element.icon ?? "plus"} onChange={(icon) => set("icon", icon === "plus" ? undefined : icon)} />
      </div>
    </div>
  );
}
